import * as React from 'react';

import { useQueryClient } from '@tanstack/react-query';

import useGetSafetyEventReport from '../api/hooks/safety-event/useGetSafetyEventReport';
import useGetAllowedActions from '../api/hooks/common/useGetAllowedActions';
import queryKeys from '../api/queryKeys';

import {
  AllowedActions,
  AuthorizationRequestType,
  SafetyEventReport,
  SafetyEventReportDTO,
  ReportType,
} from '../api/clients';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorOverlay from '../../components/ErrorOverlay';
import useSubmitSafetyEvent from '../api/hooks/safety-event/useSubmitSafetyEvent';
import useUpdateSafetyEvent from '../api/hooks/safety-event/useUpdateSafetyEvent';
import useTitle from '../api/hooks/common/useTitle';

type ISafetyEventReportData = {
  safetyEventReport: SafetyEventReport;
  allowedActions: AllowedActions | undefined;

  update: (
    data: SafetyEventReport,
    successCallback?: any,
    failCallback?: any
  ) => void;
  updateStatus: 'error' | 'idle' | 'pending' | 'success';

  submit: (
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => void;
  submitStatus: 'error' | 'idle' | 'pending' | 'success';
};

const SafetyEventReportDataContext = React.createContext<ISafetyEventReportData>(undefined!);

export function SafetyEventReportDataProvider({
  children,
  safetyEventReportId,
  role,
}: {
  children: React.ReactNode,
  safetyEventReportId: number,
  role?: AuthorizationRequestType,
}) {
  const queryClient = useQueryClient();
  useTitle(document.documentURI);
  const {
    data: reportData,
    status: reportStatus,
    error: reportError,
  } = useGetSafetyEventReport(safetyEventReportId, role || AuthorizationRequestType.Contribute);

  const { data: allowedActions } = useGetAllowedActions(
    reportData?.employeeId,
    reportData?.status,
    ReportType.SafetyEventReport,
  );

  const { status: updateMutationStatus, mutate: updateMutation } = useUpdateSafetyEvent();
  const { status: submitMutationStatus, mutate: submitMutation } = useSubmitSafetyEvent();

  const onSuccessfulMutation = (
    returnData: SafetyEventReportDTO,
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => {
    const { eventReport, error } = returnData;

    queryClient.setQueryData([queryKeys.SafetyEvent.Get, { id: eventId }], eventReport);
    if (!error) {
      if (successCallback) successCallback(eventReport);
    } else if (failCallback) failCallback(error);
  };

  const safetyEventReportData = React.useMemo<ISafetyEventReportData>(() => ({
    safetyEventReport: reportData!,
    allowedActions: allowedActions!,

    updateStatus: updateMutationStatus,
    update: (data: SafetyEventReport, successCallback?: any, failCallback?: any) => {
      updateMutation(data, {
        onSuccess: (returnData) => onSuccessfulMutation(
          returnData,
          data.id,
          successCallback,
          failCallback,
        ),
      });
    },

    submitStatus: submitMutationStatus,
    submit: (eventId: number | undefined, successCallback?: any, failCallback?: any) => {
      if (eventId !== undefined) {
        submitMutation(eventId, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            eventId,
            successCallback,
            failCallback,
          ),
        });
      }
    },
  }), [
    reportData,
    allowedActions,
    updateMutationStatus,
    submitMutationStatus,
  ]);

  if (reportStatus === 'pending') {
    return (
      <LoadingOverlay />
    );
  }

  if (reportStatus === 'error') {
    return (
      <ErrorOverlay
        title={reportError?.title}
        message={`An error occurred retrieving data for form: ${safetyEventReportId}`}
      />
    );
  }

  if (reportStatus === 'success' && reportData !== undefined) {
    return (
      <SafetyEventReportDataContext.Provider value={safetyEventReportData}>
        {children}
      </SafetyEventReportDataContext.Provider>
    );
  }

  return null;
}

export const useSafetyEventReportData = () => React.useContext(SafetyEventReportDataContext);
